import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, ScrollText, Users2, Sparkles, Loader2, Eye, EyeOff } from "lucide-react";
import { LEAD_TAGS, USER_TAGS, fillScript } from "@/lib/scriptTags";

const SAMPLE_LEAD = {
  name: "Maria Gonzalez", company: "Gonzalez Trucking LLC", phone: "(555) 123-4567",
  email: "maria@gonzaleztrucking.com", city: "Columbus", state: "OH", zip: "43004",
  insurance_types: ["life", "final_needs"], premium_estimate: 1450, industry: "Transportation", employees: 12,
};

function ScriptDialog({ script, team, onClose, onDone }) {
  const { user } = useAuth();
  const [title, setTitle] = useState(script?.title || "");
  const [body, setBody] = useState(script?.body || "");
  const [assignAll, setAssignAll] = useState(script?.assign_all || false);
  const [assigned, setAssigned] = useState(script?.assigned_to || []);
  const [busy, setBusy] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiTone, setAiTone] = useState("professional");
  const [aiBusy, setAiBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const toggle = (id) => setAssigned((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const generate = async () => {
    if (!aiPrompt.trim()) return toast.error("Describe the script you want the AI to write");
    setAiBusy(true);
    try {
      const { data } = await api.post("/scripts/generate", { prompt: aiPrompt, tone: aiTone });
      if (data?.body) { setBody(data.body); setShowPreview(true); toast.success("AI draft ready — review & tweak below"); }
      else toast.error("The AI returned an empty draft. Try rephrasing.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAiBusy(false); }
  };

  const save = async () => {
    if (!title.trim() || !body.trim()) return toast.error("Title and body are required");
    setBusy(true);
    try {
      if (script) await api.patch(`/scripts/${script.script_id}`, { title, body, assign_all: assignAll, assigned_to: assigned });
      else await api.post("/scripts", { title, body, assign_all: assignAll, assigned_to: assigned });
      toast.success(script ? "Script updated" : "Script created"); onDone(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg" data-testid="script-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{script ? "Edit script" : "New script"}</DialogTitle>
          <DialogDescription>Write the script and choose who on your team can see it.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Script title (e.g. Final Expense Opener)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="script-title-input" />

          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
            <button type="button" onClick={() => setShowAI((v) => !v)} data-testid="ai-assist-toggle"
              className="flex w-full items-center gap-2 text-sm font-semibold text-primary">
              <Sparkles className="h-4 w-4" /> AI script assist {showAI ? "▲" : "▼"}
            </button>
            {showAI && (
              <div className="mt-3 space-y-2">
                <Textarea rows={2} placeholder="Describe it, e.g. 'Warm opener for a final expense lead who requested info online, empathetic tone'"
                  value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="ai-prompt-input" />
                <div className="flex items-center gap-2">
                  <Select value={aiTone} onValueChange={setAiTone}>
                    <SelectTrigger className="w-40" data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="warm & friendly">Warm & friendly</SelectItem>
                      <SelectItem value="empathetic">Empathetic</SelectItem>
                      <SelectItem value="direct & concise">Direct & concise</SelectItem>
                      <SelectItem value="high-energy">High-energy</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button type="button" size="sm" onClick={generate} disabled={aiBusy} data-testid="ai-generate-button">
                    {aiBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Generate draft
                  </Button>
                </div>
              </div>
            )}
          </div>

          <Textarea rows={8} placeholder="Write the call script here…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="script-body-input" />
          <div className="space-y-2">
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase text-muted-foreground">Lead Tags</p>
              <div className="flex flex-wrap gap-1.5">
                {LEAD_TAGS.map((t) => (
                  <button type="button" key={t.tag} title={t.label} onClick={() => setBody((b) => (b ? b + " " : "") + t.tag)}
                    data-testid={`insert-tag-${t.tag.replace(/[^a-z_]/gi, "")}`}
                    className="rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-accent">{t.tag}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase text-muted-foreground">User Tags</p>
              <div className="flex flex-wrap gap-1.5">
                {USER_TAGS.map((t) => (
                  <button type="button" key={t.tag} title={t.label} onClick={() => setBody((b) => (b ? b + " " : "") + t.tag)}
                    data-testid={`insert-tag-${t.tag.replace(/[^a-z_]/gi, "")}`}
                    className="rounded-full border border-primary/40 bg-primary/5 px-2 py-0.5 text-[11px] text-primary transition-colors hover:bg-primary/10">{t.tag}</button>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">Click a tag to insert. Lead tags fill from each lead; user tags fill from the viewing agent's profile.</p>

          <div className="rounded-lg border border-border">
            <button type="button" onClick={() => setShowPreview((v) => !v)} data-testid="script-preview-toggle"
              className="flex w-full items-center gap-2 px-3 py-2 text-sm font-medium">
              {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} Live preview (sample lead)
            </button>
            {showPreview && (
              <div className="border-t border-border px-3 py-3">
                <p className="mb-2 text-[11px] uppercase text-muted-foreground">Filled with sample lead “Maria Gonzalez” · you as agent</p>
                <p className="whitespace-pre-wrap text-sm" data-testid="script-preview-text">{fillScript(body, SAMPLE_LEAD, user) || "Nothing to preview yet."}</p>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm"><Checkbox checked={assignAll} onCheckedChange={setAssignAll} data-testid="script-assign-all" /> Assign to everyone in the agency</label>
          {!assignAll && (
            <div className="rounded-lg border border-border p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase text-muted-foreground"><Users2 className="h-3.5 w-3.5" /> Assign to</p>
              <div className="max-h-48 space-y-1.5 overflow-y-auto">
                {team.map((m) => (
                  <label key={m.user_id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={assigned.includes(m.user_id)} onCheckedChange={() => toggle(m.user_id)} data-testid={`script-assign-${m.user_id}`} /> {m.name} <span className="text-xs text-muted-foreground">· {m.role}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <Button onClick={save} disabled={busy} className="w-full" data-testid="script-save-button">{script ? "Save changes" : "Create script"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Scripts() {
  const [scripts, setScripts] = useState([]);
  const [team, setTeam] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const load = useCallback(() => { api.get("/scripts").then(({ data }) => setScripts(data)).catch(() => {}); }, []);
  useEffect(() => { load(); api.get("/team").then(({ data }) => setTeam(data)).catch(() => {}); }, [load]);
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    try { await api.delete(`/scripts/${s.script_id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6" data-testid="scripts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold sm:text-3xl">Scripts</h1>
          <p className="mt-1 text-sm text-muted-foreground">Create call scripts and assign them to your team. Agents see them on each lead.</p>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="new-script-button"><Plus className="mr-2 h-4 w-4" /> New script</Button>
      </div>
      {scripts.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground" data-testid="scripts-empty">
          <ScrollText className="mx-auto h-10 w-10 opacity-40" /><p className="mt-3 text-sm">No scripts yet. Create your first call script.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {scripts.map((s) => (
            <Card key={s.script_id} className="p-5" data-testid={`script-card-${s.script_id}`}>
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading text-lg font-semibold">{s.title}</h3>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => setEditing(s)} data-testid={`edit-script-${s.script_id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(s)} className="text-destructive" data-testid={`delete-script-${s.script_id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
              <p className="mt-2 line-clamp-4 whitespace-pre-wrap text-sm text-muted-foreground">{s.body}</p>
              <p className="mt-3 text-xs text-muted-foreground">{s.assign_all ? "Assigned to everyone" : (s.assignee_names?.length ? `Assigned to ${s.assignee_names.join(", ")}` : "Not assigned yet")}</p>
            </Card>
          ))}
        </div>
      )}
      {showNew && <ScriptDialog team={team} onClose={() => setShowNew(false)} onDone={load} />}
      {editing && <ScriptDialog script={editing} team={team} onClose={() => setEditing(null)} onDone={load} />}
    </div>
  );
}
